// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Alert, Button, Modal, Space, Spin, Tag, Typography } from "antd";
import QRCode from "qrcode";
import { Homeserver } from "../api";
import type { Session } from "../types";
import { deriveSafetyNumber, markVerified, clearVerified, verificationState, type SafetyNumber } from "../safetyNumber";

const hs = new Homeserver();

// SafetyNumberModal shows the out-of-band verification code for a conversation: derive
// it from both parties' device-key fingerprints, render the 60 digits + a QR, and let
// the user mark the contact verified after comparing in person / scanning. The verified
// state is local and tied to the peer's current key set, so a later key change shows up
// as "changed — verify again".
export function SafetyNumberModal({
  session,
  peerID,
  peerName,
  onClose,
}: {
  readonly session: Session;
  readonly peerID: string;
  readonly peerName: string;
  readonly onClose: () => void;
}) {
  const { message } = App.useApp();
  const [sn, setSn] = useState<SafetyNumber | null>(null);
  const [qr, setQr] = useState("");
  const [state, setState] = useState(verificationState(session.user_id, peerID, ""));
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        // Both fingerprint sets from the key directory.
        const dir = await hs.keysQuery(session.access_token, [session.user_id, peerID]);
        const myFps = (dir[session.user_id] ?? []).map((d) => d.fingerprint ?? "").filter(Boolean);
        const peerFps = (dir[peerID] ?? []).map((d) => d.fingerprint ?? "").filter(Boolean);
        if (myFps.length === 0 || peerFps.length === 0) {
          if (live) setErr("Both of you need a published device key before you can verify.");
          return;
        }
        const result = await deriveSafetyNumber(myFps, peerFps);
        const dataUrl = await QRCode.toDataURL(result.payload, { margin: 1, width: 220 });
        if (!live) return;
        setSn(result);
        setQr(dataUrl);
        setState(verificationState(session.user_id, peerID, result.peerDigest));
      } catch (e) {
        if (live) setErr((e as Error).message);
      }
    })();
    return () => {
      live = false;
    };
  }, [session.access_token, session.user_id, peerID]);

  function confirm() {
    if (!sn) return;
    markVerified(session.user_id, peerID, sn.peerDigest);
    setState("verified");
    message.success(`Marked ${peerName} as verified`);
  }
  function unverify() {
    clearVerified(session.user_id, peerID);
    setState("unverified");
  }

  return (
    <Modal open title={`Verify ${peerName}`} footer={null} onCancel={onClose}>
      {err && <Alert type="warning" showIcon message={err} />}
      {!err && !sn && <Spin />}
      {sn && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle" align="center">
          {state === "verified" && <Tag color="green">✓ Verified</Tag>}
          {state === "changed" && <Alert type="error" showIcon style={{ width: "100%" }} message={`${peerName}'s keys changed since you verified — compare again before trusting.`} />}
          {state === "unverified" && <Tag>Not verified</Tag>}

          <Typography.Paragraph type="secondary" style={{ marginBottom: 0, textAlign: "center" }}>
            Compare these with {peerName} in person (or have them scan the code). If both of you see the
            same emoji — or the same number — no one is intercepting your messages.
          </Typography.Paragraph>

          {/* Emoji are the quick path: 5 symbols are far faster to read aloud / eyeball
              than 60 digits, and carry the same digest. */}
          <div className="safety-emoji" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {sn.emojis.map((e, i) => (
              <div key={`${i}-${e.name}`} style={{ textAlign: "center", minWidth: 56 }}>
                <div style={{ fontSize: 38, lineHeight: 1.1 }}>{e.glyph}</div>
                <div style={{ fontSize: 11, color: "#8a8a8a", textTransform: "capitalize" }}>{e.name}</div>
              </div>
            ))}
          </div>

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            or compare the full number
          </Typography.Text>

          <div className="safety-number" style={{ fontFamily: "monospace", fontSize: 16, lineHeight: 1.8, textAlign: "center", letterSpacing: 1 }}>
            {sn.groups.map((g, i) => (
              <span key={i}>
                {g}
                {i % 4 === 3 ? <br /> : " "}
              </span>
            ))}
          </div>

          {qr && <img src={qr} alt="Safety number QR" width={200} height={200} />}

          <Space>
            {state === "verified" ? (
              <Button onClick={unverify}>Mark unverified</Button>
            ) : (
              <Button type="primary" onClick={confirm}>
                Mark as verified
              </Button>
            )}
          </Space>
        </Space>
      )}
    </Modal>
  );
}
